import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  Button,
  Grid,
  Avatar,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { updatePreferences, toggleTheme } from '../redux/slices/userSlice';

const Settings = () => {
  const dispatch = useDispatch();
  const { currentUser, theme, preferences } = useSelector((state) => state.user);

  const handlePreferenceChange = (key, value) => {
    dispatch(updatePreferences({ [key]: value }));
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account preferences and settings
      </Typography>

      {/* Profile Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Profile Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 80, height: 80, fontSize: 32 }}>
              {currentUser?.name?.charAt(0) || 'M'}
            </Avatar>
            <Box>
              <Typography variant="h6">{currentUser?.name || 'Mahmoud Digi'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser?.email || 'mahmoud@digislides.com'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              defaultValue={currentUser?.name || 'Mahmoud Digi'}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address"
              defaultValue={currentUser?.email || 'mahmoud@digislides.com'}
              type="email"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              defaultValue={currentUser?.phone || '+1 234 567 8900'}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Company Name"
              defaultValue="Digi Solutions"
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" startIcon={<SaveIcon />}>
              Save Changes
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Appearance Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Appearance
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormControlLabel
          control={
            <Switch
              checked={theme === 'dark'}
              onChange={() => dispatch(toggleTheme())}
            />
          }
          label="Dark Mode"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          Toggle between light and dark theme
        </Typography>
      </Paper>

      {/* Application Preferences */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Application Preferences
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={preferences.autoSave}
                onChange={(e) => handlePreferenceChange('autoSave', e.target.checked)}
              />
            }
            label="Auto-save menus"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
            Automatically save your menu changes
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={preferences.notifications}
                onChange={(e) => handlePreferenceChange('notifications', e.target.checked)}
              />
            }
            label="Enable notifications"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
            Receive updates about TV status and menu changes
          </Typography>

          <TextField
            select
            label="Default Menu Template"
            value={preferences.defaultMenuTemplate}
            onChange={(e) => handlePreferenceChange('defaultMenuTemplate', e.target.value)}
            SelectProps={{
              native: true,
            }}
            sx={{ maxWidth: 300 }}
          >
            <option value="classic">Classic Menu</option>
            <option value="modern">Modern Menu</option>
            <option value="minimal">Minimal Menu</option>
          </TextField>
        </Box>
      </Paper>

      {/* Account Settings */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Account
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button variant="outlined" sx={{ maxWidth: 200 }}>
            Change Password
          </Button>
          <Button variant="outlined" color="error" sx={{ maxWidth: 200 }}>
            Delete Account
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Settings;

